import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';

// Browser auth is cookie-based: gov-api sets httpOnly session cookies and its
// JWT guard reads the access cookie, so `withCredentials` is all the client
// needs — there's no token to attach by hand.
//
// Resolved at build time. In dev we fall back to the local API port; in a
// production build a missing VITE_GOV_API_URL is a misconfiguration we surface
// immediately rather than silently pointing the bundle at localhost.
const apiBaseUrl =
  import.meta.env.VITE_GOV_API_URL ?? (import.meta.env.DEV ? 'http://localhost:4501' : undefined);

if (!apiBaseUrl) {
  throw new Error('VITE_GOV_API_URL must be set for production builds of the gov app.');
}

export { apiBaseUrl };

export const api = axios.create({
  baseURL: apiBaseUrl,
  withCredentials: true,
});

type RetryableRequest = InternalAxiosRequestConfig & { _retry?: boolean };

/**
 * Auth endpoints whose own 401 must NOT trigger a refresh+retry: a bad-credential
 * login, a logout, or the refresh call itself (retrying that would loop). `/auth/me`
 * is deliberately absent — a reload with an expired access cookie but live refresh
 * cookie should silently restore the session through it.
 */
const NO_REFRESH_PATHS = ['/auth/login', '/auth/logout', '/auth/refresh'];

/** Single-flight refresh: concurrent 401s share one `/auth/refresh` round-trip. */
let refreshPromise: Promise<void> | null = null;

function refreshSession(): Promise<void> {
  refreshPromise ??= api
    .post('/auth/refresh')
    .then(() => undefined)
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
}

/**
 * Registered by `AuthProvider` so a refresh that itself fails (refresh cookie gone
 * or expired) clears client-side auth state and bounces the officer to login, instead
 * of leaving the UI "signed in" while every request 401s.
 */
/**
 * Why a session ended, when the API said: `seat-required` is an enforcement
 * seat taken away by the company administrator. Null for an ordinary expiry.
 */
export type SessionEndReason = 'seat-required' | null;

let onSessionExpired: ((reason: SessionEndReason) => void) | null = null;
export function setOnSessionExpired(handler: ((reason: SessionEndReason) => void) | null): void {
  onSessionExpired = handler;
}

/** The API's machine-readable reason for refusing a request, if it gave one. */
function refusalCode(error: AxiosError): string | null {
  const code = (error.response?.data as { code?: unknown } | undefined)?.code;
  return typeof code === 'string' ? code : null;
}

// The access cookie lives 15 min; the refresh cookie 7 days. On a 401 we rotate the
// session once via the refresh cookie and replay the original request transparently.
// Without this an officer mid-inspection starts getting failures after 15 minutes.
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetryableRequest | undefined;
    const skipRefresh = NO_REFRESH_PATHS.some((path) => original?.url?.includes(path));

    // A seat taken away ends the session outright: refreshing cannot bring it
    // back (the refresh tokens were revoked with the seat), and the sign-in
    // screen should say why rather than "your session expired".
    if (error.response?.status === 401 && refusalCode(error) === 'seat_required') {
      onSessionExpired?.('seat-required');
      return Promise.reject(error);
    }
    if (error.response?.status === 401 && original && !original._retry && !skipRefresh) {
      original._retry = true;
      try {
        await refreshSession();
        return await api(original);
      } catch (refreshError) {
        const reason =
          (refreshError as AxiosError)?.response &&
          refusalCode(refreshError as AxiosError) === 'seat_required'
            ? 'seat-required'
            : null;
        onSessionExpired?.(reason);
      }
    }
    return Promise.reject(error);
  },
);
